import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  Button,
  Text,
  Select,
  Link,
} from "@chakra-ui/react";
import { useBreakpointValue } from "@chakra-ui/react";
import Card from "./add-card";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import { SearchIcon } from "../../assets/svgs/Icons";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import {
  getIncomingApplications,
  getRcExtensionApplications,
  getRcRenewalApplications,
} from "./ContractorSlice";
import moment from "moment";
import { rankItem } from "@tanstack/match-sorter-utils";
import { useNavigate } from "react-router-dom";

function UpcomingApplications() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const dispatch = useDispatch();
  const status = "INCOMING";
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const {
    incomingApplications,
    rcExtensionApplications,
    rcRenewalApplications,
    isLoading,
  } = useSelector((state) => state.contractor);

  useEffect(() => {
    dispatch(getIncomingApplications(status));
    dispatch(getRcExtensionApplications(status));
    dispatch(getRcRenewalApplications(status));
  }, [dispatch]);

  useEffect(() => {
    const mapData = (data) =>
      data?.map((item, index) => ({
        srNo: index + 1,
        dateTime: moment(item.createdAt).format("DD-MM-YYYY"),
        id: item.id || item._id,
        name: item.name || item.scop_company?.name,
        companyScale: item.companyScale || item.scop_company?.companyScale,
        address:
          item.address?.raw_address || item?.scop_company?.address?.raw_address,
        actions: "actions",
      }));

    switch (activeTab) {
      case 0:
        setTableData(mapData(incomingApplications));
        break;
      case 1:
        setTableData(mapData(rcExtensionApplications));
        break;
      case 2:
        setTableData(mapData(rcRenewalApplications));
        break;
      default:
        setTableData([]);
    }
  }, [
    activeTab,
    incomingApplications,
    rcExtensionApplications,
    rcRenewalApplications,
  ]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const columns = [
    { header: "Sr No", accessorKey: "srNo" },
    { header: "Date", accessorKey: "dateTime" },
    { header: "Application Number", accessorKey: "id" },
    { header: "Company Name", accessorKey: "name" },
    { header: "Scale", accessorKey: "companyScale" },
    { header: "Address", accessorKey: "address" },
    { header: "Actions", accessorKey: "actions" },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box>
      <Tabs onChange={(index) => setActiveTab(index)}>
        <TabList overflowX="auto" whiteSpace="nowrap">
          <Tab>RC Applications</Tab>
          <Tab>RC Extensions</Tab>
          <Tab>RC Renewals</Tab>
        </TabList>

        <TabPanels>
          {["RC Applications", "RC Extensions", "RC Renewals"]?.map(
            (tab, idx) => (
              <TabPanel key={idx}>
                <Flex
                  justifyContent="space-between"
                  alignItems={{ base: "stretch", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                  gap={{ base: "1rem", md: "0" }}
                  mb="1rem"
                >
                  <InputGroup w={{ base: "100%", md: "400px" }}>
                    <InputLeftElement children={<SearchIcon />} />
                    <Input
                      placeholder="Search all results..."
                      value={globalFilter}
                      bg="white"
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      borderRadius={"10px"}
                      _placeholder={{
                        opacity: 1,
                        color: "#464C64",
                        fontSize: "14px",
                      }}
                    />
                  </InputGroup>

                  <Flex
                    gap="1rem"
                    alignItems={{ base: "stretch", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    w={{ base: "100%", md: "auto" }}
                  >
                    <Box
                      as="span"
                      display="flex"
                      alignItems="center"
                      gap="5px"
                      marginRight={"10px"}
                      width={{ base: "100%", md: "auto" }}
                    >
                      <Box as="span" width={{ base: "auto", md: "30%" }}>
                        {" "}
                        Go to page:{" "}
                      </Box>
                      <Input
                        w={{ base: "100%", md: "60%" }}
                        type="number"
                        color={"#464C64"}
                        bg={"#fff"}
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          table.setPageIndex(page);
                        }}
                      />
                    </Box>
                    <Select
                      bg="white"
                      w={{ base: "100%", md: "auto" }}
                      value={table.getState().pagination.pageSize}
                      onChange={(e) =>
                        table.setPageSize(Number(e.target.value))
                      }
                    >
                      {[10, 20, 30, 40, 50]?.map((size) => (
                        <option key={size} value={size}>
                          Show {size}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </Flex>

                <Box
                  as="span"
                  display="flex"
                  alignItems="center"
                  marginBottom={"10px"}
                  gap="5px"
                >
                  <div>Page</div>
                  <strong>
                    {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount()}
                  </strong>
                </Box>

                {/* Table */}
                {isMobile ? (
                  <Box>
                    {table?.getRowModel()?.rows?.map((row) => (
                      <Card
                        key={row.id}
                        data={row.original}
                        activeTab={activeTab}
                        navigate={navigate}
                      />
                    ))}
                  </Box>
                ) : (
                  <TableContainer overflowX="auto">
                    <Table bg="brand.white" borderRadius="15px">
                      <Thead>
                        <Tr>
                          {table.getHeaderGroups()?.map((headerGroup) =>
                            headerGroup.headers?.map((header) => (
                              <Th
                                key={header.id}
                                color="brand.blue"
                                textTransform="capitalize"
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {header.column.columnDef.header}
                                {header.column.getIsSorted() ? (
                                  header.column.getIsSorted() === "desc" ? (
                                    <TriangleDownIcon />
                                  ) : (
                                    <TriangleUpIcon />
                                  )
                                ) : null}
                              </Th>
                            ))
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {isLoading
                          ? [1, 2, 3, 4, 5]?.map((i) => (
                              <Tr key={i}>
                                {columns?.map((col) => (
                                  <Td key={col.header}>
                                    <Skeleton height="20px" />
                                  </Td>
                                ))}
                              </Tr>
                            ))
                          : table?.getRowModel()?.rows?.map((row) => (
                              <Tr key={row.id}>
                                {row.getVisibleCells()?.map((cell) => (
                                  <Td
                                    key={cell.id}
                                    fontWeight={500}
                                    fontSize="sm"
                                    style={{
                                      maxWidth: "250px",
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    {cell.column.id === "actions" ? (
                                      <Link
                                        color={"#3958DC"}
                                        fontWeight="800"
                                        onClick={() => {
                                          const id = encodeURIComponent(
                                            row.original.id
                                          );
                                          if (activeTab === 1) {
                                            navigate(
                                              `/contractor/rc-form-extension-details/${id}`
                                            );
                                          } else {
                                            navigate(
                                              `/contractor/rc-form-details/${id}`
                                            );
                                          }
                                        }}
                                      >
                                        View Details
                                      </Link>
                                    ) : (
                                      cell.renderValue()
                                    )}
                                  </Td>
                                ))}
                              </Tr>
                            ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}

                <Box
                  display={"flex"}
                  justifyContent="center"
                  marginTop={"1rem"}
                  marginBottom="2rem"
                >
                  <Box display={"flex"} gap="2rem">
                    <Button
                      className="border rounded p-1"
                      bg="#52BBFF"
                      _hover={{ bg: "#52BBFF" }}
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <AiOutlineArrowLeft color="#141D43" size={18} />
                    </Button>
                    <Button
                      className="border rounded p-1"
                      bg="#52BBFF"
                      _hover={{ bg: "#52BBFF" }}
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <AiOutlineArrowRight color="#141D43" size={18} />
                    </Button>
                  </Box>
                </Box>
              </TabPanel>
            )
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default UpcomingApplications;
